import { HttpMethod } from "../entities/entity_blueprints/EndPointInterface";
import { FunctionInterface } from "../entities/entity_blueprints/FunctionInterface";
import { EntityType } from "../entities/EntityType";
import { DependencyCheckResult } from "./DependencyInterfaces";
/**
 * Command Action Enum
 * Defines all available command actions
 */
export enum CommandAction {
    // Service Commands
    ADD_METHOD = "addMethod",
    REMOVE_METHOD = "removeMethod",
    UPDATE_METHOD = "updateMethod",
    ADD_SERVICE = "addService",
    REMOVE_SERVICE = "removeService",
    UPDATE_SERVICE = "updateService",

    // Middleware Commands
    ADD_MIDDLEWARE = "addMiddleware",
    REMOVE_MIDDLEWARE = "removeMiddleware",
    UPDATE_MIDDLEWARE = "updateMiddleware",
    ATTACH_FUNCTION_TO_MIDDLEWARE = "attachFunction",

    // Endpoint Commands
    ADD_ENDPOINT = "addEndpoint",
    REMOVE_ENDPOINT = "removeEndpoint",
    UPDATE_ENDPOINT = "updateEndpoint",
    ATTACH_FUNCTION_TO_ENDPOINT = ATTACH_FUNCTION_TO_MIDDLEWARE,
    ATTACH_MIDDLEWARE_TO_ENDPOINT = "attachMiddleware",
    ATTACH_SERVICE_TO_ENDPOINT = "attachService",

    // Function Commands
    ADD_FUNCTION = "addFunction",
    REMOVE_FUNCTION = "removeFunction",
    UPDATE_FUNCTION = "updateFunction",

    // Model Commands (Read-only, database synced)
    SYNC_MODEL = "syncModel",
    REMOVE_MODEL = "removeModel",

    // Relationship Commands
    ADD_DEPENDENCY = "addDependency",
    REMOVE_DEPENDENCY = "removeDependency",
}

/**
 * Command Target
 * Defines the target entity for a command operation
 */
export interface CommandTarget {
    /** For service operations */
    service?: string;

    /** For method operations */
    method?: string;

    /** For middleware operations */
    middleware?: string;

    /** For endpoint operations */
    endpoint?: {
        name: string;
        method?: HttpMethod;
        path?: string;
    };

    /** For function operations */
    functionId?: string;

    /** For model operations */
    model?: string;
}

/**
 * State Command
 * Represents a single command to be executed on the system state
 */
export interface StateCommand {
    /** Unique command ID (auto-generated if not provided) */
    id?: string;

    /** Action type */
    action: CommandAction;

    /** Target entity */
    target: CommandTarget;

    /** Function object (for addMethod, addMiddleware, addEndpoint) */
    functionObject?: FunctionInterface;

    /** Additional data for the operation */
    data?: Record<string, unknown>;

    /** Timestamp (ISO string) */
    timestamp?: string;

    /** Validation result (set during validation) */
    validation?: ValidationResult;

    /** Execution status */
    status?: 'pending' | 'validated' | 'executing' | 'completed' | 'failed' | 'rolled_back';

    /** Error message if execution failed */
    error?: string;
}

/**
 * State Trigger
 * Represents a chain of commands to be executed together
 */
export interface StateTrigger {
    /** Unique trigger ID (auto-generated if not provided) */
    id?: string;

    /** Trigger name/description */
    name?: string;

    /** Chain of commands to execute */
    commands: StateCommand[];

    /** Execution mode */
    mode?: 'sequential' | 'parallel';

    /** Stop on error */
    stopOnError?: boolean;

    /** Rollback on error */
    rollbackOnError?: boolean;

    /** Status */
    status?: 'pending' | 'executing' | 'completed' | 'failed' | 'rolled_back';

    /** Results from command execution */
    results?: CommandExecutionResult[];
}

/**
 * Validation Result
 * Result of command validation
 */
export interface ValidationResult {
    /** Whether the command is valid */
    valid: boolean;

    /** Validation errors */
    errors: ValidationError[];

    /** Validation warnings */
    warnings: ValidationWarning[];

    /** Blocking dependencies (if any) */
    blockingDependencies?: DependencyCheckResult;
}

/**
 * Validation Error
 */
export interface ValidationError {
    /** Error code */
    code: string;

    /** Error message */
    message: string;

    /** Field that caused the error (if applicable) */
    field?: string;

    /** Entity ID that caused the error (if applicable) */
    entityId?: string;

    /** Entity type that caused the error (if applicable) */
    entityType?: EntityType;
}

/**
 * Validation Warning
 */
export interface ValidationWarning {
    /** Warning code */
    code: string;

    /** Warning message */
    message: string;

    /** Suggestion for fixing the warning */
    suggestion?: string;
}

/**
 * Command Execution Result
 * Result of executing a single command
 */
export interface CommandExecutionResult {
    /** Command ID */
    commandId: string;

    /** Whether execution was successful */
    success: boolean;

    /** Error message if execution failed */
    error?: string;

    /** Affected entities */
    affectedEntities: {
        /** Created entity IDs */
        created?: string[];
        /** Modified entity IDs */
        modified?: string[];
        /** Deleted entity IDs */
        deleted?: string[];
    };

    /** Edges affected */
    edges: {
        /** Created edge IDs */
        created?: string[];
        /** Removed edge IDs */
        removed?: string[];
    };

    /** Warnings during execution */
    warnings?: string[];
}

/**
 * Trigger Execution Result
 * Result of executing a trigger (chain of commands)
 */
export interface TriggerExecutionResult {
    /** Trigger ID */
    triggerId: string;

    /** Whether execution was successful */
    success: boolean;

    /** Number of commands executed successfully */
    commandsExecuted: number;

    /** Number of commands that failed */
    commandsFailed: number;

    /** Results from each command */
    results: CommandExecutionResult[];

    /** Errors encountered */
    errors?: string[];

    /** Whether the trigger was rolled back */
    rolledBack?: boolean;
}

